import { CurrencyPipe, JsonPipe, PercentPipe } from '@angular/common';
import { Component, computed, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatLabel } from '@angular/material/form-field';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
  AnnualPercentageRate,
  AnnualRate,
  CentsPerDollar,
  Commission,
  CostOfCapital,
  DrawAmount,
  DrawFee,
  EffectivePaymentCount,
  Factor,
  LocDisbursement,
  LocPaybackAmount,
  LocPaymentAmount,
  NetRequirement,
  NumPayments,
  OriginationAmount,
  OriginationPercent,
  PayoffMonths,
  PeriodicRate,
  Points,
  StandardAmount,
  StandardDisbursement,
  StandardPayback,
  StandardPaymentAmount,
  Term,
  TotalInterest
} from '@lendiodevs/lendio-js-utilities/dist/calculator/approval-attributes';
import { FunctionalApproval } from '@lendiodevs/lendio-js-utilities/dist/calculator/functional-approval';
import {
  ApprovalAttribute,
  ApprovalAttributeDisplayTypes,
  ApprovalAttributeOverride,
  DynamicApprovalAttribute,
  DynamicApprovalAttributesMap
} from '@lendiodevs/lendio-js-utilities/dist/calculator/types/approval-attribute';
import {
  ApprovalCalculationTypes
} from '@lendiodevs/lendio-js-utilities/dist/calculator/types/approval-calculation-types';
import { toFixedNumber } from '@lendiodevs/lendio-js-utilities/dist/rounding';
import { Store } from '@ngxs/store';
import { cloneDeep, get } from 'lodash';
import { ApprovalModule } from 'ng2-app/approval/approval.module';
import {
  ApprovalAttributeInputComponent
} from 'ng2-app/approval/approval/approval-attribute/approval-attribute.component';
import { DeclineInfoDialogModule } from 'ng2-app/components/decline-info-dialog/decline-info-dialog.module';
import { DeclineReasonsDialogModule } from 'ng2-app/components/decline-reasons/decline-reasons-dialog.module';
import { RecommendOptionToggleModule } from 'ng2-app/components/recommend-option-toggle/recommend-option-toggle.module';
import { RestrictedRadioModule } from 'ng2-app/components/restricted-radio/restricted-radio.module';
import { FinancingValuePropsModule } from 'ng2-app/financing-value-props/financing-value-props.module';
import { RecommendApproval } from 'ng2-app/store/offers/approvals.actions';
import { CreateOfferOption } from 'ng2-app/store/offers/offer-options.actions';

@Component({
  selector: 'app-functional-approval',
  templateUrl: './functional-approval.component.html',
  styleUrls: ['./functional-approval.component.scss'],
  imports: [
    MatExpansionModule,
    MatButton,
    ApprovalAttributeInputComponent,
    RestrictedRadioModule,
    MatLabel,
    JsonPipe,
    ApprovalModule,
    FinancingValuePropsModule,
    RecommendOptionToggleModule,
    DeclineInfoDialogModule,
    DeclineReasonsDialogModule,
  ],
  providers: [
    PercentPipe,
  ]
})
export class FunctionalApprovalComponent implements OnInit {
  @Input() set approval(approval: any) {
    if (!this._approvalSignal) {
      this._approvalSignal = signal(approval);
    } else {
      this._approvalSignal.set(approval);
    }
  }
  get approval(): any {
    return this._approvalSignal();
  }
  _approvalSignal: WritableSignal<any>;
  @Input() showHeader: boolean = true;
  @Input() hideCreateOptionButton: boolean = false;
  @Input() canRecommend: boolean = false;

  publishedStatus = computed(() => {
    if (this.approval.declineReasons?.length > 0) {
      return 'Declined';
    } else if (this.approval.publishedByLenderUserId && this.approval.publishedToBorrower) {
      return 'Published';
    } else {
      return 'Draft';
    }
  });

  headerPreviewData = computed(() => {
    const attributes = this.functionalApproval?.attributesMap as DynamicApprovalAttributesMap;
    if(!attributes) {
      return [];
    }

    switch (this.approval.calculationType) {
      case 'standard':
      case 'maxPayment':
        const standardAmount = this.functionalApproval.getAttributeInitialValue('amount');
        return [
          {
            label: 'AMOUNT',
            value: this.currencyPipe.transform(standardAmount),
          },
          {
            label: 'POINTS',
            value: this.functionalApproval.getAttributeInitialValue('points'),
          },
          {
            label: 'FACTOR',
            value: this.functionalApproval.getAttributeInitialValue('factor'),
          },
        ];
      case 'term':
      case 'loc':
      case 'sba':
        const drawAmount = this.functionalApproval.getAttributeInitialValue('drawAmount');
        const numStips = (this.approval.requestNames?.conditional?.length || 0) + (this.approval.requestNames?.nonConditional?.length || 0);
        return [
          {
            label: 'LIMIT',
            value: this.currencyPipe.transform(drawAmount),
          },
          {
            label: 'TERM~',
            value: this.functionalApproval.maxTerm,
          },
          {
            label: 'Cents / $',
            value: this.functionalApproval.getAttributeInitialValue('centsPerDollar'),
          },
          {
            label: 'STIPS',
            value: numStips,
          },
          {
            label: 'POINTS',
            value: this.functionalApproval.getAttributeInitialValue('points'),
          },
        ];
      default:
        return [];
    }
  });

  outputAttributes = computed(() => {
    const outputAttributeAliases = (this.getOutputAttributes() || []).map(att => att.alias);
    const functionalAttributes = [...this.functionalApproval.attributes];
    return functionalAttributes.filter(att => outputAttributeAliases.includes(att.alias));
  });

  outputsDisplayData = signal([]);

  availableFrequencyValues = computed(() => {
    const availableFrequencies = [...this.functionalApproval.availablePaymentFrequencies];
    return availableFrequencies.map(availableFrequency => availableFrequency.value);
  });

  inputableAttributes = computed(() => {
    const attributes = this.getInputAttributes() || [];
    return attributes.map(att =>
      this.functionalApproval.attributesMap[att.alias as string]
    );
  });

  functionalApproval: FunctionalApproval;

  constructor(
    private currencyPipe: CurrencyPipe,
    private store: Store,
  ) {}

  ngOnInit() {
    const attributeOverrides: ApprovalAttributeOverride[] = [
      {
        alias: 'apr',
        label: '~ APR',
      },
      {
        alias: 'annualRate',
        label: 'Interest Rate',
      },
      {
        alias: 'paymentAmount',
        label: 'Payment',
      },
      {
        alias: 'effectivePaymentCount',
        label: '# Payments',
      }
    ];

    if(this.approval.calculationType === ApprovalCalculationTypes.sba) {
      attributeOverrides.push({
        alias: 'drawFee',
        label: 'Packaging Fee',
      });
    }

    this.functionalApproval = new FunctionalApproval(
      this.approval,
      () => {
        this.updateOutputsDisplayData();
      },
      attributeOverrides,
    );

    if (['standard', 'maxPayment'].includes(this.approval.calculationType)) {
      const maxOption = cloneDeep(this.functionalApproval.getMaxOption());
      const avgPoints = Number(this.approval.points);

      if (avgPoints > 0 && maxOption?.limits?.points?.max > avgPoints) {
        maxOption.limits.points.max = avgPoints;
        this.functionalApproval.maxOption = maxOption;
        this.functionalApproval.setAttributeValue('points', avgPoints, true);
      }
    }

    setTimeout(() => this.updateOutputsDisplayData());
  }

  changePaymentFrequency(value: string) {
    this.functionalApproval.setPaymentFrequencyFromStringValue(value);
  }

  getInputAttributes() {
    switch (this.approval.calculationType) {
      case 'standard':
      case 'maxPayment':
        return [
          Term,
          Points,
          StandardAmount,
          OriginationPercent,
        ];
      case 'sba':
        return [
          Term,
          DrawAmount,
          Points,
        ];
      case 'term':
      case 'loc':
        return [
          Term,
          DrawAmount,
          PayoffMonths,
        ];
    }
  }

  getOutputAttributes() {
    switch (this.approval.calculationType) {
      case 'standard':
      case 'maxPayment':
        return [
          Term,
          StandardAmount,
          StandardPaymentAmount,
          Factor,
          StandardPayback,
          NumPayments,
          OriginationAmount,
          StandardDisbursement,
          Commission,
          NetRequirement,
        ];
      case 'sba':
        return [
          DrawAmount,
          AnnualRate,
          StandardPaymentAmount,
          PeriodicRate,
          LocDisbursement,
          Commission,
          NumPayments,
          DrawFee,
        ];
      case 'term':
      case 'loc':
        return [
          DrawAmount,
          AnnualRate,
          LocPaymentAmount,
          PeriodicRate,
          LocDisbursement,
          DrawFee,
          Commission,
          AnnualPercentageRate,
          CentsPerDollar,
          LocPaybackAmount,
          EffectivePaymentCount,
          TotalInterest,
          CostOfCapital,
        ];
    }
  }

  updateOutputsDisplayData(): void {
    this.outputsDisplayData.set(this.getOutputsDisplayData() as any);
  }

  getOutputsDisplayData() {
    const outputAttributes = this.outputAttributes() || [];
    return outputAttributes.map(attr => {
      const attribute = this.functionalApproval.attributesMap[attr.alias as string];
      const value = this.getOutputDisplayValueForAttribute(attribute);
      return {
        label: attribute.label,
        value,
      };
    });
  }

  getOutputDisplayValueForAttribute(attribute: ApprovalAttribute) {
    const value = attribute.currentValue;
    switch (attribute.displayType) {
      case ApprovalAttributeDisplayTypes.currency:
        return this.currencyPipe.transform(value || 0);
      case ApprovalAttributeDisplayTypes.percent:
        return `${toFixedNumber(value, 3, 10)} %`;
      case ApprovalAttributeDisplayTypes.number:
        return toFixedNumber(value, 2, 10);
      default:
        return value;
    }
  }

  createOfferOption() {
    if (this.approval.dealId) {
      const offerOptionData = this.getOfferOptionData();
      this.store.dispatch(new CreateOfferOption(this.approval.dealId || 0, offerOptionData));
    }
  }

  recommendedToggleChange(event: MatSlideToggleChange) {
    this.store.dispatch(new RecommendApproval(this.approval.id, event.checked));
  }

  getOfferOptionData() {
    const attsMap = this.functionalApproval.attributesMap;
    const termSetDataByCalcType = this.getTermSetDataByCalcType();
    return {
      approvalId: this.approval.id,
      termSet: {
        term: attsMap.term.currentValue,
        termType: this.approval.termType,
        paymentFrequency: this.functionalApproval.selectedPaymentFrequency.value,
        paymentCount: attsMap.numPayments.currentValue,
        paymentAmount: attsMap.paymentAmount.currentValue,
        payback: attsMap.payback.currentValue,
        disbursement: attsMap.disbursement.currentValue,
        position: this.approval.position,
        points: attsMap.points.currentValue,
        commission: attsMap.commission.currentValue,
        ...termSetDataByCalcType,
      },
    };
  }

  getTermSetDataByCalcType() {
    const attsMap = this.functionalApproval.attributesMap;
    const selectedOption = this.functionalApproval.selectedOption;
    switch (this.approval.calculationType) {
      case 'standard':
      case 'maxPayment':
        return {
          amount: attsMap.amount.currentValue,
          factorRate: attsMap.factor.currentValue,
          originationFeePercent: attsMap.originationPercent.currentValue,
          originationFeeAmount: attsMap.originationAmount.currentValue,
        };
      case 'term':
      case 'sba':
        return {
          amount: attsMap.drawAmount.currentValue,
          originationFeePercent: get(selectedOption, 'drawFeePercent'),
          originationFeeAmount: attsMap.drawFee.currentValue,
        };
      case 'loc':
        const drawAmount = attsMap.drawAmount as DynamicApprovalAttribute;
        return {
          initialDrawAmount: drawAmount.currentValue,
          creditLimit: drawAmount.currentBoundaries?.max,
          drawFeePercent: get(selectedOption, 'drawFeePercent'),
          drawFeeAmount: attsMap.drawFee.currentValue,
        };
    }
  }

}
